import { isInterventionPlanRetired } from "./interventionStatus.js";

// Explicit existing work carries its own application. The workspace application
// is a default for starting work, not authority to move an older plan's work.
export const resolveInterventionWorkApplicationId = (plans, {
  interventionId = null, planId = null, applicationId = null,
} = {}) => {
  const list = Array.isArray(plans) ? plans : [];
  if (interventionId) {
    for (const plan of list) {
      const item = (plan.interventions || []).find(row => String(row.id) === String(interventionId));
      if (item) return item.applicationId ?? item.application_id ??
        item.proposalApplicationId ?? item.proposal_application_id ??
        plan.applicationId ?? plan.application_id ?? null;
    }
  }
  const plan = planId ? list.find(row => String(row.id) === String(planId)) : null;
  return plan ? (plan.applicationId ?? plan.application_id ?? null) : applicationId;
};

export const isActionPlanSelectableForApplication = (plan, applicationId) => {
  const planApplicationId = Number(plan?.applicationId ?? plan?.application_id);
  const activeApplicationId = Number(applicationId);
  const hasPlanApplication = Number.isInteger(planApplicationId) && planApplicationId > 0;
  const hasActiveApplication = Number.isInteger(activeApplicationId) && activeApplicationId > 0;
  if (!hasPlanApplication && !hasActiveApplication) {
    return plan?.historicalManual === true || plan?.historical_manual === true;
  }
  return (
    hasPlanApplication &&
    hasActiveApplication &&
    planApplicationId === activeApplicationId
  );
};

// Compatibility proposals may retain application lineage on an older case-owned
// plan. Their exact application still owns the work; sibling proposals do not.
export const isInterventionInApplicationScope = (intervention, plan, applicationId) => {
  if (applicationId === undefined) return true;
  const itemApplicationId = Number(intervention?.applicationId ?? intervention?.application_id ??
    intervention?.proposalApplicationId ?? intervention?.proposal_application_id);
  const planApplicationId = Number(plan?.applicationId ?? plan?.application_id);
  const target = Number(applicationId);
  if (itemApplicationId > 0) {
    return target > 0 && itemApplicationId === target &&
      (!(planApplicationId > 0) || planApplicationId === target);
  }
  return isActionPlanSelectableForApplication(plan, applicationId);
};

// Only current work in the selected application may drive automatic selection or
// block a new action. Retain all original plans separately for historical views.
export const getInterventionWorkingPlans = (plans, applicationId, { includeClosed = false } = {}) =>
  (Array.isArray(plans) ? plans : []).filter(plan => !isInterventionPlanRetired(plan) ||
    (includeClosed && String(plan.status).toLowerCase() === 'closed' && !plan.archivedAt && !plan.archived_at))
    .map(plan => ({
      ...plan,
      interventions: (plan.interventions || []).filter(item =>
        item?.operational !== false &&
        item?.isAppliedRevisionEvidence !== true &&
        item?.is_applied_revision_evidence !== true &&
        isInterventionInApplicationScope(item, plan, applicationId)
      ),
    })).filter(plan => applicationId === undefined ||
      isActionPlanSelectableForApplication(plan, applicationId) || plan.interventions.length > 0);
